import { Box, Stack, Theme, Typography, alpha } from '@mui/material';
import { useFormikContext } from 'formik';
import { format } from 'date-fns';
import { EmployeeOffboardingAppointmentHandling } from '@prisma/client';
import {
  OffboardingWizardValues,
  StepId,
} from '../../../../types/offboardings';
import { staggerSx } from '../../../../helpers/styles/offboardings';

interface ReviewStepProps {
  goTo: (id: StepId) => void;
}

function ReviewStep({ goTo }: ReviewStepProps) {
  const { values } = useFormikContext<OffboardingWizardValues>();

  const whenText =
    values.mode === 'IMMEDIATE'
      ? 'Immediately'
      : values.scheduledFor
      ? format(new Date(values.scheduledFor), "PPP 'at' p")
      : 'Not set';
  const appointmentsText =
    values.appointmentHandling ===
    EmployeeOffboardingAppointmentHandling.STAY_AT_SHOP
      ? 'Stay at the shop'
      : 'Transfer with the artist';
  const accessText =
    values.retainCustomerAccess === 'true'
      ? 'Retain customer access'
      : 'Restrict customer access';

  const rows: Array<{ id: StepId; label: string; value: string }> = [
    { id: 'when', label: 'Timing', value: whenText },
    { id: 'appointments', label: 'Appointments', value: appointmentsText },
    { id: 'access', label: 'Customer access', value: accessText },
  ];

  return (
    <Stack gap={2.25} sx={staggerSx}>
      <Box
        sx={(theme: Theme) => ({
          background: '#fff',
          border: `1px solid ${theme.palette.darkerLightGrey.main}`,
          borderRadius: '14px',
          overflow: 'hidden',
        })}>
        {rows.map((row, idx) => (
          <Box
            key={row.id}
            sx={(theme: Theme) => ({
              display: 'grid',
              gridTemplateColumns: '180px 1fr auto',
              alignItems: 'baseline',
              gap: 2.25,
              p: '18px 22px',
              borderBottom:
                idx === rows.length - 1
                  ? 'none'
                  : `1px solid ${alpha(
                      theme.palette.darkerLightGrey.main,
                      0.45,
                    )}`,
              [theme.breakpoints.down(720)]: {
                gridTemplateColumns: '1fr',
                gap: 0.75,
              },
            })}>
            <Typography variant="overline" color="text.secondary">
              {row.label}
            </Typography>
            <Typography variant="h4">{row.value}</Typography>
            <Box
              component="button"
              type="button"
              onClick={() => goTo(row.id)}
              sx={(theme: Theme) => ({
                appearance: 'none',
                background: 'transparent',
                border: 'none',
                color: 'text.secondary',
                fontFamily: 'inherit',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                cursor: 'pointer',
                p: 0,
                transition: theme.transitions.create(['color']),
                '&:hover': { color: 'text.primary' },
                [theme.breakpoints.down(720)]: { justifySelf: 'start' },
                typography: 'caption',
                fontWeight: 600,
              })}>
              Edit
            </Box>
          </Box>
        ))}
      </Box>
    </Stack>
  );
}

export default ReviewStep;
